import { Download, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router";

import { Logo } from "@/components/common/Logo";
import { usePendingReviews } from "@/features/timesheets/TimesheetTabs";
import { Tooltip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import { useMe } from "../auth";
import { navItems, type NavItem } from "../navigation";

type SidebarProps = {
  collapsed: boolean;
  onToggle: () => void;
};

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const { t } = useTranslation();
  const me = useMe();
  const pending = usePendingReviews();
  const allowed = navItems.filter((i) => !i.permission || me.permissions[i.permission]);
  const groups = (["main", "manage", "dev"] as const)
    .map((group) => ({ group, items: allowed.filter((i) => i.group === group) }))
    .filter((g) => g.items.length > 0);

  return (
    <aside
      className={cn(
        "sticky top-0 flex h-dvh shrink-0 flex-col border-r bg-surface transition-[width] duration-200",
        collapsed ? "w-16" : "w-60",
      )}
    >
      <div className={cn("flex h-16 items-center", collapsed ? "justify-center" : "px-5")}>
        <Logo collapsed={collapsed} />
      </div>

      <nav aria-label={t("nav.mainMenu")} className="flex-1 overflow-y-auto px-3 py-2">
        {groups.map(({ group, items }, i) => (
          <ul key={group} className={cn("flex flex-col gap-0.5", i > 0 && "mt-3 border-t pt-3")}>
            {items.map((item) => (
              <li key={item.key}>
                <SidebarLink
                  item={item}
                  collapsed={collapsed}
                  badge={item.key === "timesheets" ? pending : undefined}
                />
              </li>
            ))}
          </ul>
        ))}
      </nav>

      <div className="flex flex-col gap-0.5 border-t p-3">
        <SidebarLink
          item={{ key: "downloadTracker", path: "/download", icon: Download, group: "main" }}
          collapsed={collapsed}
        />
        <SidebarButton
          icon={collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
          label={collapsed ? t("nav.expand") : t("nav.collapse")}
          collapsed={collapsed}
          onClick={onToggle}
        />
      </div>
    </aside>
  );
}

const itemClass =
  "flex h-9 w-full items-center gap-3 rounded-lg px-2.5 font-medium text-muted transition-colors hover:bg-surface-muted hover:text-fg [&_svg]:size-[18px] [&_svg]:shrink-0";

type LinkItem = Pick<NavItem, "path" | "icon"> & {
  key: NavItem["key"] | "downloadTracker";
  group: NavItem["group"];
};

function SidebarLink({ item, collapsed, badge }: { item: LinkItem; collapsed: boolean; badge?: number }) {
  const { t } = useTranslation();
  const name = t(`nav.${item.key}`);
  const label = badge ? `${name} (${t("timesheets.pendingCount", { count: badge })})` : name;
  const Icon = item.icon;

  const link = (
    <NavLink
      to={item.path}
      end={item.path === "/"}
      aria-label={collapsed ? label : undefined}
      className={({ isActive }) =>
        cn(
          itemClass,
          collapsed && "justify-center px-0",
          isActive && "bg-primary-soft text-primary hover:bg-primary-soft hover:text-primary",
        )
      }
    >
      <span className="relative flex">
        <Icon aria-hidden="true" />
        {collapsed && badge ? (
          <span
            className="absolute -top-1 -right-1 size-2 rounded-full bg-warning-solid"
            aria-hidden="true"
          />
        ) : null}
      </span>
      {!collapsed && <span className="flex-1 truncate">{name}</span>}
      {!collapsed && badge ? (
        <span className="rounded-full bg-warning-soft px-1.5 text-xs text-warning tabular-nums">
          <span aria-hidden="true">{badge}</span>
          <span className="sr-only">{t("timesheets.pendingCount", { count: badge })}</span>
        </span>
      ) : null}
    </NavLink>
  );

  // The tooltip trigger wraps the link instead of using it directly: Radix Slot merges
  // className as a string and would break NavLink's className function.
  return collapsed ? (
    <Tooltip content={label} side="right">
      <span className="block">{link}</span>
    </Tooltip>
  ) : (
    link
  );
}

type SidebarButtonProps = {
  icon: ReactNode;
  label: string;
  collapsed: boolean;
  disabled?: boolean;
  onClick?: () => void;
};

function SidebarButton({ icon, label, collapsed, disabled, onClick }: SidebarButtonProps) {
  const button = (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={collapsed ? label : undefined}
      className={cn(
        itemClass,
        "cursor-pointer disabled:cursor-not-allowed disabled:opacity-50",
        collapsed && "justify-center px-0",
      )}
    >
      {icon}
      {!collapsed && <span className="truncate">{label}</span>}
    </button>
  );
  return collapsed && !disabled ? (
    <Tooltip content={label} side="right">
      {button}
    </Tooltip>
  ) : (
    button
  );
}
